import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, ArrowRight, BellRing, Sparkles } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import JobCard, { Listing } from '@/components/JobCard';
import AccountNotices from '@/components/AccountNotices';
import JobAlertsPanel from '@/components/JobAlertsPanel';
import CreditsStrip from '@/components/contractor/CreditsStrip';
import PurchasedLeadsPreview from '@/components/contractor/PurchasedLeadsPreview';
import SavedLeadsPanel from '@/components/contractor/SavedLeadsPanel';
import UpgradeOffers from '@/components/UpgradeOffers';
import WebsiteRequestCard from '@/components/contractor/WebsiteRequestCard';
import { fetchListings, fetchPurchasedIds } from '@/lib/marketplace';
import { useLeadBalance } from '@/lib/creditsBus';
import { localizedSlug, BASIC_INTRO_OFFER, introOfferLine, money, planLabel, PLAN_PRICES } from '@/data/constants';

/**
 * CONTRACTOR DASHBOARD — only what helps a contractor buy leads, see the
 * leads they paid for, or manage their membership. Top to bottom:
 *   1. Credits (how many leads can I unlock right now)
 *   2. MY PURCHASED LEADS — every paid lead with contact details
 *   3. Upgrade path (Pro / Premium) or the Basic intro offer
 *   4. Find leads — 3 open projects (matches first) + "see all"
 *   5. New-lead alerts (collapsed)
 */
const SubDashboard: React.FC = () => {
  const { t, path, lang } = useI18n();
  const navigate = useNavigate();
  const es = lang === 'es';
  const { profile } = useAuth();
  const { balance } = useLeadBalance(profile?.id);
  const [jobs, setJobs] = useState<Listing[]>([]);
  const [purchasedIds, setPurchasedIds] = useState<string[]>([]);

  useEffect(() => {
    fetchListings({ limit: 12 }).then(setJobs).catch(() => setJobs([]));
    fetchPurchasedIds(profile?.id).then(setPurchasedIds).catch(() => setPurchasedIds([]));
  }, [profile?.id]);

  const myTrades = profile?.trades || [];
  const myAreas = (profile?.service_areas || []).map((s) => s.toLowerCase());
  const open = jobs.filter((j) => !purchasedIds.includes(j.id));
  const matches = open.filter(
    (j) => myTrades.includes(j.trade) || myAreas.includes((j.city || '').toLowerCase()),
  );
  const shown = (matches.length ? [...matches, ...open.filter((j) => !matches.includes(j))] : open).slice(0, 3);
  // In-app navigation (was window.location.assign — a full page reload that
  // re-downloaded the app and flashed a loading screen on phones).
  const goTo = (j: Listing) => navigate(path(`/jobs/${localizedSlug(j.slug, j.trade, lang)}`));

  return (
    <div>
      <CreditsStrip />
      <AccountNotices />

      <PurchasedLeadsPreview />

      {/* SAVED LEADS — bookmarks only; purchase still runs through the normal lead page. */}
      <SavedLeadsPanel credits={balance.spendable} />

      {/* PREMIUM: "FREE website included" — status + short intake form (renders only if a request exists). */}
      <WebsiteRequestCard />

      {/* UPGRADE PATH — Basic sees Pro + Premium, Pro sees Premium, Premium sees nothing.
          No membership: the Basic intro offer ($50 first month). */}
      <div className="mb-8">
        {balance.hasSubscription ? (
          <UpgradeOffers currentPlan={balance.planCode} placement="dashboard" compact={false} />
        ) : (
          <section className="rounded-2xl border-2 border-emerald-300 bg-emerald-50 p-4 sm:p-5">
            <p className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-2.5 py-0.5 text-[11px] font-black uppercase tracking-wide text-white">
              <Sparkles className="h-3 w-3" /> {es ? 'Oferta de introducción' : 'Intro offer'}
            </p>
            <h2 className="mt-2 text-lg font-extrabold text-slate-900 sm:text-xl">
              {es
                ? `Membresía ${planLabel(BASIC_INTRO_OFFER.plan, lang)}: primer mes ${money(BASIC_INTRO_OFFER.firstMonthCents)}`
                : `${planLabel(BASIC_INTRO_OFFER.plan, lang)} Membership: first month ${money(BASIC_INTRO_OFFER.firstMonthCents)}`}
              <span className="ml-2 text-sm font-semibold text-slate-500 line-through">{money(PLAN_PRICES[BASIC_INTRO_OFFER.plan])}</span>
            </h2>
            <p className="mt-1 text-sm text-slate-700">{introOfferLine(lang)}</p>
            <Link
              to={`${path('/pricing')}?plan=${BASIC_INTRO_OFFER.plan}`}
              className="mt-3 inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 px-5 text-sm font-bold text-white hover:bg-emerald-700 sm:w-auto"
            >
              {es ? `Empezar por ${money(BASIC_INTRO_OFFER.firstMonthCents)}` : `Start for ${money(BASIC_INTRO_OFFER.firstMonthCents)}`} <ArrowRight className="h-4 w-4" />
            </Link>
          </section>
        )}
      </div>

      <section>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="flex items-center gap-2 text-xl font-extrabold text-slate-900">
              <Search className="h-5 w-5 text-orange-500" /> {es ? 'Buscar leads' : 'Find leads'}
            </h2>
            <p className="mt-0.5 text-sm text-slate-500">
              {matches.length
                ? es
                  ? 'Proyectos abiertos que coinciden con sus oficios y zonas.'
                  : 'Open projects that match your trades and areas.'
                : es
                ? 'Los proyectos abiertos más recientes.'
                : 'The newest open projects.'}
            </p>
          </div>
          <Link to={path('/jobs')} className="inline-flex min-h-[44px] items-center gap-1.5 text-sm font-bold text-orange-600">
            {t('common.viewAll')} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        {shown.length > 0 ? (
          <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {shown.map((j) => (
              <JobCard key={j.id} job={j} unlocked={false} onUnlock={() => goTo(j)} credits={balance.spendable} />
            ))}
          </div>
        ) : (
          /* No open projects loaded (or all already bought): never an empty
             gap under the heading — one clear way forward. */
          <Link
            to={path('/jobs')}
            className="mt-4 flex min-h-[56px] items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-slate-300 bg-white px-5 text-sm font-bold text-slate-700 hover:border-orange-300 hover:text-orange-700"
          >
            <Search className="h-4 w-4" /> {es ? 'Ver todos los proyectos abiertos' : 'See all open projects'}
          </Link>
        )}
      </section>

      <details className="group mt-10 rounded-2xl border border-slate-200 bg-white">
        <summary className="flex min-h-[56px] cursor-pointer list-none items-center gap-2 px-5 text-sm font-bold text-slate-800">
          <BellRing className="h-4 w-4 text-orange-500" />
          {es ? 'Recibir alertas de nuevos leads' : 'Get alerts for new leads'}
          <ArrowRight className="ml-auto h-4 w-4 text-slate-400 transition-transform group-open:rotate-90" />
        </summary>
        <div className="border-t border-slate-100 px-2 pb-4 sm:px-4">
          <JobAlertsPanel />
        </div>
      </details>
    </div>
  );
};

export default SubDashboard;
